import React from "react";
import { observer } from "mobx-react";
import styles from "./ProductionStagesList.module.scss";
import { columns } from "./columns";
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage";
import { DefaultAgGridOptions, defaultModules } from "modules/common/services/table/helpers";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { agGridEntityContextMenu } from "modules/common/components/table/EntityContextMenu";
import { ProductionStageList, ProductionStagesListType } from "../../models/production-stage-list";
import { ProductionStageSnapshotType, formatProductionStage } from "../../models/production-stage";
import copyToClipboard from "copy-to-clipboard";
import {
  GridReadyEvent,
  RowClickedEvent,
  GridOptions,
  CellContextMenuEvent,
  GetContextMenuItemsParams,
} from "@ag-grid-community/core";
import { agGridLocale } from "modules/root/services/locale";
import { AgGridReact } from "@ag-grid-community/react/lib/agGridReact";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { MAX_INT } from "modules/common/constants";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { PlanrButton } from "modules/common/components/planr/button/Button";

const modules = [...defaultModules, MenuModule];
const TABLE_NAME = ProductionStageList.name;
const sizeSaver = saveAgGridColumnSize(TABLE_NAME);
const config = columns(TABLE_NAME);

export const ProductionStagesList = observer(
  class extends React.Component<ProductionStagesListProps> {
    private options: GridOptions = DefaultAgGridOptions();

    render() {
      const { store, onSelected } = this.props;

      return (
        <RemoveConfirmation<ProductionStageSnapshotType>
          observation={store.data}
          what={this.removeLabel}
          onConfirmed={this.removeStage}
          render={({ confirmRemoving }) => {
            const getContextMenuItems = ({ node }: GetContextMenuItemsParams) =>
              agGridEntityContextMenu({
                onCopy: () => copyToClipboard(formatProductionStage(node.data), { format: "text/plain" }),
                onChange: () => onSelected(getRowNodeId(node.data)),
                onRemove: () => confirmRemoving(node.data),
              });

            return (
              <>
                <GridTheme bordered={true} className={`designed ${styles.grid}`}>
                  <AgGridReact
                    rowStyle={{
                      borderBottom: "1 px solid #EDF0F2",
                      fontFamily: "GothamPro, Arial, sans-serif",
                      color: "#00273D",
                      height: "32px",
                      fontSize: "12px",
                    }}
                    rowSelection="single"
                    suppressCellSelection={true}
                    enableCellTextSelection={true}
                    columnDefs={config}
                    rowData={store.data}
                    localeText={agGridLocale}
                    onRowDoubleClicked={this.onRowSelected}
                    getRowNodeId={getRowNodeId}
                    getContextMenuItems={getContextMenuItems}
                    onGridReady={this.onGridReady}
                    onCellContextMenu={this.onCellContextMenu}
                    onSortChanged={store.sorter.resorted}
                    onColumnResized={sizeSaver}
                    gridOptions={this.options}
                    modules={modules}
                    rowBuffer={MAX_INT}
                  />
                </GridTheme>
                <div className={styles.cardList}>
                  {store.data.map((row) => {
                    return (
                      <div key={row.id}>
                        <div className={styles.card}>
                          <div className={styles.mid}>
                            <div className={styles.label}>Наименование:</div>
                            <div className={styles.row}>{row.name}</div>

                            {row.comment && (
                              <>
                                <div className={styles.label}>Описание:</div>
                                <div className={styles.row}>{row.comment}</div>
                              </>
                            )}
                          </div>
                          <div className={styles.edit}>
                            <PlanrButton
                              type="lightblueish"
                              icon="general-edit"
                              size="small"
                              onClick={() => {
                                this.props.onSelected(row.id);
                              }}
                            />
                            <PlanrButton
                              type="neutral"
                              icon={"general-trash"}
                              size="small"
                              style={{ marginLeft: "0px", marginTop: "5px", color: "#e31818" }}
                              onClick={() => {
                                confirmRemoving(row);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          }}
        />
      );
    }

    removeLabel = (stage: ProductionStageSnapshotType) => `стадию строительства ${stage.name}`;

    removeStage = async (stage: ProductionStageSnapshotType) => {
      const { store, onRemoved } = this.props;
      const success = await store.delete(stage);
      success && onRemoved();
    };

    onCellContextMenu = ({ node }: CellContextMenuEvent) => {
      node && node.setSelected(true, true);
    };

    onRowSelected = ({ data }: RowClickedEvent) => {
      const { onSelected } = this.props;
      onSelected(getRowNodeId(data));
    };

    onGridReady = async ({ api }: GridReadyEvent) => {
      const { sorter } = this.props.store;
      setTimeout(() => api.setSortModel(sorter.agGridSortModel));
    };
  }
);

const getRowNodeId = (e: ProductionStageSnapshotType) => e.id;

interface ProductionStagesListProps {
  onRemoved: () => void;
  onSelected: (id: string) => void;
  store: ProductionStagesListType;
}
